import { useTranslation } from "react-i18next";
import { useDocument } from "~/api/v2/useDocument";
import { useDocumentVersion } from "~/api/v2/useDocumentVersion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { DocumentFieldEditForm } from "./DocumentFieldEditForm";
import { DocumentVersionFieldEditForm } from "./DocumentVersionFieldEditForm";
import { getEditableFieldLabel } from "./columnRegistry";
import { isVersionEditableField } from "~/helpers/libraryEditing";
import type {
  ActiveFieldEdit,
  DocumentEditableField,
  VersionEditableField,
} from "~/helpers/libraryTypes";

interface FieldEditDialogProps {
  activeEdit: ActiveFieldEdit | null;
  onOpenChange: (isOpen: boolean) => void;
  onSaved: () => Promise<void>;
}

export const FieldEditDialog = ({
  activeEdit,
  onOpenChange,
  onSaved,
}: FieldEditDialogProps) => {
  const { t } = useTranslation();
  const isVersionField = activeEdit
    ? isVersionEditableField(activeEdit.field)
    : false;
  const {
    data: document,
    isLoading,
    error,
    mutate: mutateDocument,
  } = useDocument(isVersionField ? undefined : activeEdit?.documentId);
  const {
    data: documentVersion,
    isLoading: isDocumentVersionLoading,
    error: documentVersionError,
    mutate: mutateDocumentVersion,
  } = useDocumentVersion(isVersionField ? activeEdit?.versionId : undefined);
  const isDialogLoading = isVersionField ? isDocumentVersionLoading : isLoading;
  const dialogError = isVersionField ? documentVersionError : error;
  const handleSaved = async () => {
    if (isVersionField) {
      await Promise.all([onSaved(), mutateDocumentVersion()]);
      return;
    }

    await Promise.all([onSaved(), mutateDocument()]);
  };

  return (
    <Dialog open={activeEdit !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {activeEdit
              ? `${t("commons.buttons.edit")} ${getEditableFieldLabel(activeEdit.field, t)}`
              : ""}
          </DialogTitle>
        </DialogHeader>

        {!activeEdit ? null : isDialogLoading ? (
          <CenterSpinner />
        ) : dialogError ? (
          <NoResult message={t("commons.errors.noDocument")} />
        ) : isVersionField ? (
          documentVersion ? (
            <DocumentVersionFieldEditForm
              documentVersion={documentVersion}
              field={activeEdit.field as VersionEditableField}
              onCancel={() => onOpenChange(false)}
              onSaved={handleSaved}
            />
          ) : (
            <NoResult message={t("commons.errors.noDocument")} />
          )
        ) : !document ? (
          <NoResult message={t("commons.errors.noDocument")} />
        ) : (
          <DocumentFieldEditForm
            key={activeEdit.field}
            document={document}
            field={activeEdit.field as DocumentEditableField}
            onCancel={() => onOpenChange(false)}
            onSaved={handleSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
